import {Component, Input, OnChanges, OnInit, SimpleChanges} from '@angular/core';
import {ActivatedRoute} from "@angular/router";
import {ChapterApiService} from '../api/chapter-api.service';
import {RoleLabels} from '../enums/role-labels';

import {ToastrService} from 'ngx-toastr';
import {CommitteeService} from '../api/committee-api.service';
import {UserApiService} from '../api/user-api.service';
import {API_URL} from '../constants';
import {SpinnerService} from '../shared/services/spinner.service';
import {finalize} from 'rxjs';
@Component({
    selector: 'app-user-card',
    templateUrl: './user-card.component.html',
    styleUrl: './user-card.component.css'
})
export class UserCardComponent implements OnInit, OnChanges {
    @Input() loadOnInit = true;
    @Input() users: any[] | null = null;
    @Input() initialSelectedUserId: string | null = null;
    @Input() chapterScopeId: string | null = null;

    chapterId!: string;
    chapterList: any[] = [];
    chapterUsers: any[] = [];
    filteredUsers: any[] = [];
    showUserDropdown = false;
    selectedUser: any = null;
    selectedUserWithAdditionalDetails: any = null;
    uploadedImageUrl: string | null = null; // <-- for profile image
    usersLoaded = false;
    private loadingUsers = false;

    constructor(
        private route: ActivatedRoute,
        private toastr: ToastrService,
        private chapterApiService: ChapterApiService,
        private userApiService: UserApiService,
        private spinner: SpinnerService,
        private committeeService: CommitteeService,
    ) {
        this.chapterId = this.route.snapshot.paramMap.get('id')!;
    }

    ngOnInit(): void {
        if (this.users && this.users.length > 0) {
            this.chapterUsers = [...this.users];
            this.usersLoaded = true;
            this.selectInitialUserIfAvailable();
            return;
        }

        if (this.loadOnInit) {
            this.ensureUsersLoaded();
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['users'] && this.users && this.users.length > 0) {
            this.chapterUsers = [...this.users];
            this.usersLoaded = true;
            this.selectInitialUserIfAvailable();
        } else if (changes['users'] && this.users && this.users.length === 0) {
            this.chapterUsers = [];
            this.usersLoaded = false;
        }

        if (changes['initialSelectedUserId'] && this.usersLoaded) {
            this.selectInitialUserIfAvailable();
        }
    }

    ensureUsersLoaded(force = false): void {
        if (this.users && this.users.length > 0 && !force) {
            this.chapterUsers = [...this.users];
            this.usersLoaded = true;
            this.selectInitialUserIfAvailable();
            return;
        }

        if (this.usersLoaded && !force) {
            return;
        }

        if (this.loadingUsers) {
            return;
        }

        this.loadingUsers = true;
        this.spinner.show();
        const usersRequest = this.chapterScopeId
            ? this.userApiService.getUsersByChapterId(this.chapterScopeId)
            : this.userApiService.getUsers();

        usersRequest.pipe(
            finalize(() => this.spinner.hide())
        ).subscribe({
            next: (response) => {
                this.chapterUsers = response || [];
                this.usersLoaded = true;
                this.selectInitialUserIfAvailable();
                this.loadingUsers = false;
            },
            error: (err) => {
                this.loadingUsers = false;
                console.error('Failed to load chapter users', err);
                this.toastr.error('Failed to load users');
            }
        });
    }

    onEmailInput(event: any) {
        if (!this.usersLoaded) {
            this.ensureUsersLoaded();
        }
        const input = event.target.value?.toLowerCase() || '';
        this.filteredUsers = this.chapterUsers.filter(user =>
            (user.firstname + ' ' + user.lastname + ' ' + user.email).toLowerCase().includes(input)
        );
        this.showUserDropdown = !!input && this.filteredUsers.length > 0;
    }

    selectUser(user: any) {
        // Work on a copy: the parent passes its directory list in by reference (@Input users),
        // so mutating this object below corrupts the Member Directory's membership label
        // (e.g. a NON_MEMBER row gets relabelled "Member"). See rana-web#30.
        const selected = {...user};
        this.selectedUser = selected;
        this.showUserDropdown = false;
        this.selectedUserWithAdditionalDetails = null;

        // Fetch image if user has a photo
        if (user.photouri && user.photouri !== 'null') {
            const imageUrl = `${API_URL}profile/viewimage/${user.id}/${user.photouri}`;
            this.userApiService.getProfileImageAsBlob(imageUrl).subscribe(
                (blob: Blob) => {
                    if (blob && blob.size > 0) {
                        if (this.uploadedImageUrl) {
                            URL.revokeObjectURL(this.uploadedImageUrl);
                        }
                        this.uploadedImageUrl = URL.createObjectURL(blob);
                    } else {
                        this.uploadedImageUrl = null;
                        console.warn('No image available for this user.');
                    }
                },
                (error) => {
                    this.uploadedImageUrl = null;
                    console.error('Failed to load image preview:', error);
                }
            );
        } else {
            this.uploadedImageUrl = null;
        }


        this.chapterApiService.getChapterData(user.chapterAffiliation).subscribe(
            (response) => {
                if (selected.userType) {
                    selected.userType = selected.userType + ', ' + response.name;
                }


                this.committeeService.getCommitteesByChapterId(user.chapterAffiliation).subscribe({
                    next: (data) => {
                        for (let i = 0; i < data.length; i++) {
                            if (data[i].userId == user.id && data[i].title) {
                                selected.userType = data[i].title + ', ' + response.name;
                            }
                        }
                    },
                    error: (err) => {
                        console.error('Error fetching committee:', err);
                    },
                });

                selected.chapterAffiliation = response.name;

                selected.role = this.getRoleLabel(user.role) || user.role;
                selected.memberType = this.getRoleLabel(user.memberType) || user.role;
            },
            (error) => {
                console.error('Failed to load chapters', error);
            }
        );

    }

    getRoleLabel(role: string): string {
        return RoleLabels[role as keyof typeof RoleLabels] || role;
    }

    userInitials(user: any): string {
        const first = (user?.firstname || '').charAt(0);
        const last = (user?.lastname || '').charAt(0);
        return (first + last).toUpperCase() || '?';
    }
    getAdditionalDetails(){
        this.spinner.show();
        this.userApiService.getData(this.selectedUser.id).pipe(
            finalize(() => this.spinner.hide()) // ✅ Hide spinner after outermost call completes
        ).subscribe((data) => {
            this.selectedUserWithAdditionalDetails = data;
        });

    }
    hideDropdownWithDelay() {
        setTimeout(() => this.showUserDropdown = false, 200);
    }

    onSearchFocus() {
        this.showUserDropdown = true;
        if (!this.usersLoaded) {
            this.ensureUsersLoaded();
        }
    }

    private selectInitialUserIfAvailable() {
        if (!this.initialSelectedUserId || !this.chapterUsers.length) {
            return;
        }
        const initialUser = this.chapterUsers.find((user) => user.id === this.initialSelectedUserId);
        if (initialUser) {
            this.selectUser(initialUser);
        }
    }
}
